import { observer } from "mobx-react-lite";
import { useState } from "react";

import { Stack } from "@bps/fluent-ui";
import { CommunicationKind } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { CommunicationType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { CalendarEventReminderSearchModel } from "@stores/booking/models/CalendarEventReminderSearchModel.ts";
import { formatCommunicationValue } from "@stores/core/models/Communication.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Navigate } from "@ui-components/navigation/Navigate.tsx";

import { AddMobileModal } from "./AddMobileModal.tsx";
import { UpdateConsentModal } from "./UpdateConsentModal.tsx";

interface EventReminderListPhoneProps {
  item: CalendarEventReminderSearchModel;
  onSaved: () => void;
}

type OpenModal = "mobile" | "consent" | null;

export const EventReminderListPhone: React.FC<EventReminderListPhoneProps> =
  observer(({ item, onSaved }) => {
    const { core } = useStores();
    const country = core.tenantDetails?.country;
    const [openModal, setOpenModal] = useState<OpenModal>(null);

    const apptRemPreferences =
      item.patient?.contactPreferences?.apptRemPreferences;

    const optedOut = apptRemPreferences?.contactHasOptedOut;
    const consentNotSet = !apptRemPreferences?.preferredCommChannelTypeCode;
    const hasMobileOnFile = item.patient?.communications?.some(
      c => c.type === CommunicationType.Mobile
    );

    const handleSaved = () => {
      onSaved();
      setOpenModal(null);
    };

    if (!hasMobileOnFile) {
      return (
        <Stack styles={{ root: { textAlign: "right" } }}>
          <Navigate onClick={() => setOpenModal("mobile")}>Add mobile</Navigate>
          {openModal === "mobile" && (
            <AddMobileModal
              item={item}
              onDismiss={() => setOpenModal(null)}
              onSaved={handleSaved}
            />
          )}
        </Stack>
      );
    } else if (optedOut || consentNotSet) {
      return (
        <Stack styles={{ root: { textAlign: "right" } }}>
          <Navigate onClick={() => setOpenModal("consent")}>
            Update consent
          </Navigate>
          {openModal === "consent" && (
            <UpdateConsentModal
              item={item}
              onDismiss={() => setOpenModal(null)}
              onSaved={handleSaved}
            />
          )}
        </Stack>
      );
    } else {
      const format = formatCommunicationValue(
        item.phoneNumber ?? "",
        CommunicationKind.Phone,
        country
      );
      return <Stack styles={{ root: { textAlign: "right" } }}>{format}</Stack>;
    }
  });
